import { supabase } from './supabase';
import { OrderFormData } from '../components/OrderForm';

export interface Order {
  id: string;
  date: string;
  items: {
    id: string;
    name: string;
    price: number;
    quantity: number;
  }[];
  total: number;
  status: 'pending' | 'paid' | 'cancelled';
  customer_info: OrderFormData;
  payment_method: string;
  expiration_date: string;
  cancellation_date: string;
}

class OrderService {
  private static instance: OrderService;
  private orders: Order[] = [];

  private constructor() {}

  public static getInstance(): OrderService {
    if (!OrderService.instance) {
      OrderService.instance = new OrderService();
    }
    return OrderService.instance;
  }

  public async createOrder(items: Order['items'], customerInfo: OrderFormData, paymentMethod: string): Promise<Order> {
    const orderDate = new Date();
    const expirationDate = new Date(orderDate);
    expirationDate.setDate(orderDate.getDate() + 5);
    const cancellationDate = new Date(orderDate);
    cancellationDate.setDate(orderDate.getDate() + 3);

    const newOrder: Order = {
      id: crypto.randomUUID(),
      date: orderDate.toISOString(),
      items,
      total: items.reduce((sum, item) => sum + item.price * item.quantity, 0),
      status: 'pending',
      customer_info: customerInfo,
      payment_method: paymentMethod,
      expiration_date: expirationDate.toISOString(),
      cancellation_date: cancellationDate.toISOString(),
    };

    console.log('Nouvelle commande à créer:', newOrder);

    // Sauvegarder dans Supabase
    const { data, error } = await supabase
      .from('orders')
      .insert([{
        id: newOrder.id,
        date: newOrder.date,
        items: newOrder.items,
        total: newOrder.total,
        status: newOrder.status,
        customer_info: newOrder.customer_info,
        payment_method: newOrder.payment_method,
        expiration_date: newOrder.expiration_date,
        cancellation_date: newOrder.cancellation_date
      }])
      .select()
      .single();

    if (error) {
      console.error('Erreur lors de la création de la commande:', error);
      throw error;
    }

    console.log('Commande créée avec succès:', data);
    return data;
  }

  public async getOrder(orderId: string): Promise<Order | null> {
    const { data, error } = await supabase
      .from('orders')
      .select('*')
      .eq('id', orderId)
      .single();

    if (error) {
      console.error('Erreur lors de la récupération de la commande:', error);
      return null;
    }

    return data;
  }

  public async getAllOrders(): Promise<Order[]> {
    const { data, error } = await supabase
      .from('orders')
      .select('*')
      .order('date', { ascending: false });

    if (error) {
      console.error('Erreur lors de la récupération des commandes:', error);
      return [];
    }

    return data;
  }

  public async updateOrderStatus(orderId: string, status: Order['status']): Promise<void> {
    const { error } = await supabase
      .from('orders')
      .update({ status })
      .eq('id', orderId);

    if (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
      throw error;
    }
  }

  public async getPendingOrders(): Promise<Order[]> {
    const { data, error } = await supabase
      .from('orders')
      .select('*')
      .eq('status', 'pending')
      .order('date', { ascending: true });

    if (error) {
      console.error('Erreur lors de la récupération des commandes en attente:', error);
      return [];
    }

    return data;
  }

  public async getPaidOrders(): Promise<Order[]> {
    const { data, error } = await supabase
      .from('orders')
      .select('*')
      .eq('status', 'paid')
      .order('date', { ascending: false });

    if (error) {
      console.error('Erreur lors de la récupération des commandes payées:', error);
      return [];
    }

    return data;
  }

  public async getCancelledOrders(): Promise<Order[]> {
    const { data, error } = await supabase
      .from('orders')
      .select('*')
      .eq('status', 'cancelled')
      .order('date', { ascending: false });

    if (error) {
      console.error('Erreur lors de la récupération des commandes annulées:', error);
      return [];
    }

    return data;
  }
}

export const orderService = OrderService.getInstance(); 